import { gsap } from 'gsap';
import { SplitText } from 'gsap/SplitText';

gsap.registerPlugin(SplitText);

/**
 * Complete hero section animation for page-to-page transitions
 * Used when returning to homepage from another page (no intro animation)
 * This is completely independent from IntroAnimation and can work standalone
 */
export const animateHeroSection = async () => {
  // Get all hero elements
  const heroTitle = document.querySelector('.hero-title');
  const heroSubtitle = document.querySelector('.hero-subtitle');
  const heroButtons = document.querySelectorAll('.hero-get-started');
  const heroTitleLine = document.querySelector('.hero-title-line');
  const heroBooksBackground = document.querySelector('.hero-books-background');
  const underButtonText = document.querySelector('.under-button-text');
  const worksWithAll = document.querySelector('.works-with-all');

  // Wait for fonts to be loaded before creating SplitText
  try {
    const fonts = (document as any)?.fonts;
    if (fonts && fonts.status !== 'loaded') {
      await Promise.race([
        fonts.ready,
        new Promise<void>((resolve) => setTimeout(resolve, 1000))
      ]);
    }
  } catch {
    // Ignore errors and continue
  }

  // Wait for layout to be complete using requestAnimationFrame
  // This ensures line breaks are calculated correctly after navigation
  await new Promise(resolve => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(resolve, 50);
      });
    });
  });

  // Check for reduced motion preference
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  
  // Ensure title is always accessible to screen readers and read mode browsers
  if (heroTitle) {
    (heroTitle as HTMLElement).setAttribute('aria-hidden', 'false');
    (heroTitle as HTMLElement).style.visibility = 'visible';
    (heroTitle as HTMLElement).style.opacity = '1';
  }

  // Split title into lines using GSAP SplitText
  // Check if title was already split to avoid double splitting
  let lineInners: HTMLElement[] = [];
  if (heroTitle && !(heroTitle as any)._splitTextProcessed && !prefersReducedMotion) {
    const split = new SplitText(heroTitle, { type: 'lines' });
    
    // Mark as processed to prevent double splitting
    (heroTitle as any)._splitTextProcessed = true;

    // Ensure all split lines are accessible
    (split.lines as HTMLElement[]).forEach((line: HTMLElement) => {
      line.setAttribute('aria-hidden', 'false');
      line.style.visibility = 'visible';
    });

    // Create inner wrappers for masking effect
    (split.lines as HTMLElement[]).forEach((line: HTMLElement) => {
      line.style.overflow = 'hidden';
      line.style.marginTop = '-0.1em'; // Reduce gap between lines
      line.style.marginBottom = '-0.1em'; // Reduce gap between lines
      
      const inner = document.createElement('span');
      inner.style.display = 'inline-block';
      inner.style.willChange = 'transform';
      inner.style.rotate = '0deg';
      inner.style.lineHeight = '130%'; // Keep original line-height
      // Ensure inner spans are accessible
      inner.setAttribute('aria-hidden', 'false');
      
      while (line.firstChild) {
        inner.appendChild(line.firstChild);
      }
      line.appendChild(inner);
      lineInners.push(inner);
    });
  }

  // Set initial visibility for all elements
  if (heroTitle) gsap.set(heroTitle, { opacity: 1, visibility: 'visible' });
  if (heroTitleLine) gsap.set(heroTitleLine, { y: '50px', opacity: 0, visibility: 'visible' });
  if (heroSubtitle) gsap.set(heroSubtitle, { y: '50px', opacity: 0, visibility: 'visible' });
  if (heroButtons.length > 0) {
    heroButtons.forEach(btn => {
      if (btn instanceof HTMLElement) {
        btn.style.display = ''; // Clear display: none
        gsap.set(btn, { y: '20px', opacity: 0, visibility: 'visible' });
      }
    });
  }
  if (heroBooksBackground) gsap.set(heroBooksBackground, { opacity: 0, visibility: 'visible' });
  if (underButtonText) gsap.set(underButtonText, { y: '20px', opacity: 0, visibility: 'visible' });
  if (worksWithAll) gsap.set(worksWithAll, { y: '20px', opacity: 0, visibility: 'visible' });

  // Create main timeline
  const tl = gsap.timeline();

  // 1. Fade in books background first
  if (heroBooksBackground) {
    tl.to(heroBooksBackground, {
      opacity: 1,
      duration: 0.6,
      ease: "power2.out",
    }, 0);
  }

  // 2. Animate title lines (if available and not already animated)
  if (lineInners.length > 0) {
    tl.from(lineInners, {
      yPercent: -100,
      rotate: -5,
      duration: 0.6,
      stagger: 0.03,
      ease: 'power3.out',
      clearProps: 'transform,rotate'
    }, 0);
  }

  // 3. Animate subtitle and title line together
  const heroElements = [heroSubtitle, heroTitleLine].filter(Boolean);
  if (heroElements.length > 0) {
    tl.to(heroElements, {
      y: '0px',
      opacity: 1,
      duration: 0.6,
      stagger: 0.03,
      ease: "power2.out",
    }, '<');
  }

  // 4. Animate buttons
  if (heroButtons.length > 0) {
    tl.to(heroButtons, {
      y: '0px',
      opacity: 1,
      duration: 0.6,
      stagger: 0.03,
      ease: "power2.out",
    }, '<+=0.03');
  }

  // 5. Animate under-button text
  if (underButtonText) {
    tl.to(underButtonText, {
      y: '0px',
      opacity: 1,
      duration: 0.5,
      ease: "power2.out",
    }, '-=0.3');
  }

  // 6. Animate works-with-all
  if (worksWithAll) {
    tl.to(worksWithAll, {
      y: '0px',
      opacity: 1,
      duration: 0.5,
      ease: "power2.out",
    }, '-=0.3');
  }

  return tl;
};

/**
 * Legacy export for backward compatibility
 * @deprecated Use animateHeroSection instead
 */
export const animateHeroTitle = animateHeroSection;


